import React, { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { PageHeader, EditorialButton } from "@/lib/editorial";
import { settingsApi } from "@/lib/api";
import { RefreshCw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const { data: settings = [] } = useQuery({ queryKey: ["settings"], queryFn: settingsApi.get });
  const [draft, setDraft] = useState({});
  const [confirm, setConfirm] = useState(false);

  const saveMut = useMutation({
    mutationFn: () => settingsApi.save(Object.fromEntries(Object.entries(draft).filter(([, v]) => v.trim()))),
    onSuccess: () => { toast("Settings saved to /etc/outreach-v2/.env."); setDraft({}); },
    onError: (e) => toast.error(e.message || "Save failed."),
  });

  const restartMut = useMutation({
    mutationFn: settingsApi.restart,
    onSuccess: () => { setConfirm(false); toast("Service restarting…"); },
    onError: (e) => toast.error(e.message || "Restart failed."),
  });

  return (
    <div className="px-8 lg:px-14 py-12 max-w-[1200px]">
      <PageHeader
        eyebrow="Global"
        tape="Settings"
        rest="& secrets"
        sub="Edit API credentials and model selections. Values write to /etc/outreach-v2/.env on the server. ADMIN_PASSWORD and ADMIN_SESSION_SECRET must be set via SSH only."
        testId="settings-title"
      />

      <div className="border border-[var(--rule-light)] bg-white divide-y divide-[var(--rule-light)]">
        {settings.map((s) => (
          <div key={s.key} className="grid grid-cols-1 md:grid-cols-12 gap-6 px-6 py-6" data-testid={`setting-${s.key}`}>
            <div className="md:col-span-4">
              <div className="font-mono text-[12px] flex items-center gap-2">
                {s.key}
                <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${s.set ? "text-emerald-600 border-emerald-300" : "text-rose-600 border-rose-300"}`}>
                  {s.set ? "set" : "missing"}
                </span>
              </div>
            </div>
            <div className="md:col-span-8">
              <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--muted-light)] mb-1">Current</div>
              <div className="font-mono text-[13px] mb-3">{s.masked}</div>
              <input
                placeholder="New value (leave blank to keep existing)"
                value={draft[s.key] ?? ""}
                onChange={(e) => setDraft({ ...draft, [s.key]: e.target.value })}
                className="w-full bg-transparent border-0 border-b border-[var(--ink)]/30 focus:border-[var(--blue)] outline-none py-2 font-mono text-[13px]"
                data-testid={`setting-input-${s.key}`}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 flex items-center gap-4">
        <EditorialButton onClick={() => saveMut.mutate()} data-testid="settings-save">
          Save changes
        </EditorialButton>
        <span className="text-[12px] text-[var(--muted-light)]">
          {Object.keys(draft).length} pending change{Object.keys(draft).length === 1 ? "" : "s"}
        </span>
      </div>

      <section className="mt-16 bg-[var(--ink)] text-white p-10">
        <div className="text-[10px] uppercase tracking-[0.24em] text-white/55 mb-3">Apply changes</div>
        <h2 className="font-display text-[34px] uppercase leading-none">
          <span className="tape dark">Restart</span> <span className="italic">service</span>
        </h2>
        <div className="mt-3 h-[2px] w-12 bg-[var(--blue)]" />
        <p className="mt-4 max-w-xl text-white/65 text-[14px] leading-relaxed">
          Restarts the FastAPI process (and its in-process tick scheduler) so newly saved .env values are picked up. Expect ~2 seconds of downtime.
        </p>
        {!confirm ? (
          <button
            onClick={() => setConfirm(true)}
            className="mt-6 inline-flex items-center gap-2 border border-white/40 text-white px-5 py-3 text-[11px] uppercase tracking-[0.22em] hover:bg-white hover:text-[var(--ink)] transition-colors"
            data-testid="settings-restart"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Restart service
          </button>
        ) : (
          <div className="mt-6 inline-flex items-center gap-4">
            <span className="text-amber-300 text-[12px] uppercase tracking-[0.18em] flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Confirm restart?
            </span>
            <button
              onClick={() => restartMut.mutate()}
              className="bg-[var(--blue)] hover:bg-[var(--blue-hover)] text-white px-5 py-2.5 text-[11px] uppercase tracking-[0.22em]"
              data-testid="settings-restart-confirm"
            >
              Yes, restart
            </button>
            <button
              onClick={() => setConfirm(false)}
              className="text-white/55 hover:text-white text-[11px] uppercase tracking-[0.22em]"
            >
              Cancel
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
